import axios from "axios";
import _ from "lodash";
import { DateTime } from "luxon";
import { requestCalendarSettingsForAddEditScreenFromService } from "../../calendar/actions/calendarSettingsActions_AddEditEvent";
import { ConfirmRejectPost } from "../../modernSocialMediaAlertsManagement/components/ConfirmRejectPost";
import { IImageUploadData } from "../classes/ImageUploadData";
import { IConfig } from "../Config";

export default class DataService {
  public config: IConfig;
  private cachedToken: any;

  constructor(config: IConfig) {
    this.config = config || ({} as IConfig);
    this.cachedToken = null;
  }

  public getNewsByFriendlyUrl = async (
    friendlyUrl: string,
    feedIds: string[],
    archived = false,
    includeAllData = false,
  ) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/News/${this.config.environmentId}/${this.config.currentServerId}/url/${friendlyUrl}?districtServerId=${this.config.districtServerId}&page=1&pageSize=1000&isallfeeds=${this.config.isAllFeeds}&ptlId=${this.config.portletInstanceId}&archived=${Number(archived)}&includeAllData=${includeAllData}&${_.join(_.map(feedIds, (v) => `feeds=${v}`), "&")}`,
      await this.getConfig(),
    )

  public getNewsByNewsId = async (
      newsId: string,
      feedIds: string[],
      archived = false,
      includeAllData = false,
    ) =>
      await axios.get(
        `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/News/${this.config.environmentId
        }/${this.config.currentServerId
        }/Feeds/${newsId}?page=1&pageSize=1000&isallfeeds=${this.config.isAllFeeds}&ptlId=${this.config.portletInstanceId}&archived=${Number(archived)}&includeAllData=${includeAllData}&${_.join(_.map(feedIds, (v) => `feeds=${v}`), "&")}`,
        await this.getConfig(),
      )

  public getNewsById = async (newsId) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/News/${this.config.environmentId}/${this.config.districtServerId}/${newsId}`,
      await this.getConfig(),
    )

  public getNews = async (newsId) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/News/${newsId}`,
      await this.getConfig(),
    )

  public getAllNews = async (
    page: number = 1,
    pageSize: number = 30,
    includeDeleted: boolean = false,
    includeAllData: boolean = false
  ) =>
    await axios.get(
      `${_.trimEnd(
        this.config.newsroomApiBaseUrl,
        "/",
      )}/api/News?page=${page}&pageSize=${pageSize}&includeDeletedData=${includeDeleted}&includeAllData=${includeAllData}`,
      await this.getConfig(),
    )

  public getAllFeedNews = async (
    feedId: string,
    page: number = 1,
    pageSize: number = 30,
    archived = false,
    includeAllData = false,
    categories: string[] = null,
    tags: string[] = null
  ) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/News/${this.config.environmentId
      }/${this.config.currentServerId
      }/Feed/${feedId}?page=${page}&pageSize=${pageSize}&ptlId=${this.config.portletInstanceId}&archived=${archived}&includeAllData=${includeAllData}${!_.isEmpty(categories)
        ? `&${_.join(_.map(categories, (v) => `categories=${v}`), "&")}`
        : ""
      }${!_.isEmpty(tags) ? `&${_.join(_.map(tags, (v) => `tags=${v}`), "&")}` : ""
      }`,
    )

  public getAllFeedsNews = async (
    feedIds: string[],
    page: number = 1,
    pageSize: number = 30,
    archived = false,
    includeAllData = false,
    categories: string[] = null,
    tags: string[] = null,
    searchText: string = null,
    showPostsAfter: string = null,
  ) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/News/${this.config.environmentId
      }/${this.config.currentServerId
      }/Feeds/?page=${page}&pageSize=${pageSize}&ptlId=${this.config.portletInstanceId}&isallfeeds=${this.config.isAllFeeds }&archived=${Number(archived)}&includeAllData=${includeAllData}${!_.isEmpty(categories)
        ? `&${_.join(_.map(categories, (v) => `categories=${v}`), "&")}`
        : ""
      }${!_.isEmpty(tags) ? `&${_.join(_.map(tags, (v) => `tags=${v}`), "&")}` : ""
      }&${_.join(_.map(feedIds, (v) => `feeds=${v}`), "&")}&${!_.isEmpty(searchText) ? `searchText=${searchText}` : ""}&${!_.isEmpty(showPostsAfter) ? `showPostsAfter=${showPostsAfter}` : ""}`,
      await this.getConfig(),
    )

  public getAllAnonymousNews = async (
    page: number = 1,
    pageSize: number = 30,
    archived = false,
    includeAllData = false,
    categories: string[] = null,
    tags: string[] = null,
    searchText: string = null,
    showPostsAfter: string = null,
  ) =>
    this.getAllFeedsNews(
      this.config.feedIds,
      page,
      pageSize,
      archived,
      includeAllData,
      categories,
      tags,
      searchText,
      showPostsAfter,
    );

  // lagacy method to retrieve post fron one feed
  // this.getAllFeedNews(
  //   this.config.feedId,
  //   page,
  //   pageSize,
  //   includeDeletedData,
  //   includeAllData,
  //   categories,
  //   tags,
  // );

  public deleteNews = async (newsId, isPermanent = false) =>
    await axios.delete(
      `${_.trimEnd(
        this.config.newsroomApiBaseUrl,
        "/",
      )}/api/News/${newsId}?ispermanent=${isPermanent}`,
      await this.getConfig(true),
    )

  public saveNews = async (post) =>
    await axios.post(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/News`,
      post,
      await this.getConfig(true),
    )

  public updateNews = async (newsId, post) =>
    await axios.put(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/News/${newsId}`,
      post,
      await this.getConfig(true),
    )

  public getTagsByFeeds = async (
    page: number = 1,
    pageSize: number = 30,
  ) =>
      await axios.get(
        `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/Tags/${this.config.environmentId
        }/${this.config.districtServerId
        }/Feeds?page=${page}&pageSize=${pageSize}&ptlId=${this.config.portletInstanceId}&isallfeeds=${this.config.isAllFeeds}&${_.join(_.map(this.config.feedIds, (v) => `feeds=${v}`), "&")}`,
        await this.getConfig(),
        )

  public addCategory = async (category) =>
    await axios.post(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/Categories`,
      category,
      await this.getConfig(true),
    )

  public deleteCategory = async (categoryId) =>
    await axios.delete(
      `${_.trimEnd(
        this.config.newsroomApiBaseUrl,
        "/",
      )}/api/v2/Categories/${categoryId}`,
      await this.getConfig(true),
    )

  public getCategory = async (categoryId) =>
    await axios.get(
      `${_.trimEnd(
        this.config.newsroomApiBaseUrl,
        "/",
      )}/api/Categories/${categoryId}`,
      await this.getConfig(),
    )

  public getAnonymousCategories = async (
    page = 1,
    pageSize = 30,
    includeActiveNews = 0
  ) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/Categories/${this.config.environmentId
      }/${this.config.districtServerId
      }?page=${page}&pageSize=${pageSize}&includeActiveNews=${includeActiveNews}&ptlId=${this.config.portletInstanceId}`,
      await this.getConfig(),
    )

  public getAnonymousCategoriesByFeeds = async (
      page = 1,
      pageSize = 30,
      includeActiveNews = 0,
    ) =>
      await axios.get(
        `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/Categories/${this.config.environmentId
        }/${this.config.districtServerId
        }/Feeds?page=${page}&pageSize=${pageSize}&isallfeeds=${this.config.isAllFeeds}&ptlId=${this.config.portletInstanceId}&includeActiveNews=${includeActiveNews}&${_.join(_.map(this.config.feedIds, (v) => `feeds=${v}`), "&")}`,
        await this.getConfig(),
      )

  public getAnonymousCategoriesForFeed = async (
    page = 1,
    pageSize = 30,
    includeDeletedData = false
  ) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/Categories/${this.config.environmentId
      }/${this.config.districtServerId}/Feed/${this.config.feedIds[0]
      }?page=${page}&pageSize=${pageSize}&includeDeletedData=${includeDeletedData}`,
      await this.getConfig(),
    )

  public getCategories = async (
    page = 1,
    pageSize = 30,
    includeDeletedData = false
  ) =>
    await axios.get(
      `${_.trimEnd(
        this.config.newsroomApiBaseUrl,
        "/",
      )}/api/v2/Categories/${this.config.environmentId
      }/${this.config.districtServerId}?page=${page}&pageSize=${pageSize}&includeDeletedData=${includeDeletedData}`,
      await this.getConfig(),
    )

  public updateCategory = async (categoryId, category: any) =>
    await axios.put(
      `${_.trimEnd(
        this.config.newsroomApiBaseUrl,
        "/",
      )}/api/v2/Categories/${categoryId}`,
      category,
      await this.getConfig(true),
    )

  public getWebsites = async () =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/User/websites`,
      await this.getConfig(),
    )

  public addFeed = async (feedName: string) =>
    await axios.post(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/Feeds`,
      { feedName },
      await this.getConfig(true),
    )

  public deleteFeed = async (feedId) =>
    await axios.delete(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/Feeds/${feedId}`,
      await this.getConfig(true),
    )

  public getAnonymousFeeds = async (publishOnly = false) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/Feeds/${this.config.environmentId}/${this.config.currentServerId}?publishOnly=${publishOnly}&ptlId=${this.config.portletInstanceId}`,
    )

  public getFeed = async (feedId) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/Feeds/${this.config.environmentId}/${this.config.districtServerId}/${feedId}`,
      await this.getConfig(),
    )

  public getFeeds = async (
    page = 1,
    pageSize = 30,
    includeDeletedData = false
  ) =>
    await axios.get(
      `${_.trimEnd(
        this.config.newsroomApiBaseUrl,
        "/",
      )}/api/Feeds?page=${page}&pageSize=${pageSize}&includeDeletedData=${includeDeletedData}`,
      await this.getConfig(),
    )

  public updateFeed = async (feedId, feed: any) =>
    await axios.put(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/Feeds/${feedId}`,
      feed,
      await this.getConfig(true),
    )

  public getUser = async () =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/User`,
      await this.getConfig(),
    )

  public getAccessToken = async (invalidateCachedToken: boolean = false) => {
    if (this.config.role === "user") {
      return "";
    }
    if (!_.isEmpty(sessionStorage.getItem("token"))) {
      try {
        const token = JSON.parse(sessionStorage.getItem("token")) || {};
        if (DateTime.fromISO(token.expires).toLocal() > DateTime.local()) {
          this.cachedToken = token.accessToken;
          return token.accessToken || "";
        }
      } catch (e) {
        // tslint:disable-next-line:no-empty
      }
    }

    if (!invalidateCachedToken && this.cachedToken != null) {
      try {
        if (DateTime.fromISO(this.cachedToken.expires).toLocal() > DateTime.local()) {
          return this.cachedToken.accessToken || "";
        }
      } catch (e) {
        // tslint:disable-next-line:no-empty
      }
    }

    const token =
      (await axios.get(
        `${_.trimEnd(this.config.newsroomServiceURL, "/")}/AccessToken`,
      )).data || {};
    this.cachedToken = token;
    return token.accessToken || "";
  }

  public uploadFeaturedImage = (upload: IImageUploadData) => {
    const data = new FormData();
    data.append("file", upload.file);
    const headers = new Headers();
    headers.append("Accept", "application/json, text/javascript, */*; q=0.01");
    return fetch(
      `${_.trimEnd(this.config.newsroomServiceURL, "/")}/FeaturedImage?x=${upload.x}&y=${upload.y}&w=${upload.w}&h=${upload.h}`,
      {
        body: data,
        cache: "no-store",
        credentials: "same-origin",
        headers,
        method: "POST",
      },
    );
  }

  public getSettings = async () =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomServiceURL, "/")}/Settings/${this.config.portletInstanceId
      }`,
    )

  public getRenderTemplates = async (category) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomServiceURL, "/")}/RenderTemplates/${category}`,
    )

  public getRenderTemplate = async (category, templateId: string) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomServiceURL, "/")}/RenderTemplates/${category}/${templateId}`,
    )

  public updateSettings = async (settings: any) =>
    await axios.put(
      `${_.trimEnd(this.config.newsroomServiceURL, "/")}/Settings/${this.config.portletInstanceId
      }`,
      settings,
    )

  public getFeedWebsites = async (feedId) =>
    await axios.get(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/FeedWebsites/${this.config.environmentId}/${this.config.currentServerId}/${feedId}`,
      await this.getConfig(),
    )

  public getAllSubscribedAndPublishedSites = async (feedId: string) => {
    const allSites = (await this.getWebsites()).data || [];
    let subscribedSites = [];
    const resultSites = [];

    if (feedId) {
      subscribedSites = (await this.getFeedWebsites(feedId)).data || [];
    }

    if (allSites.serverId !== undefined && allSites.serverId > 0) {
      let subscribedSite = null;
      let siteData = null;

      if (subscribedSites.length > 0) {
        subscribedSite = subscribedSites.find((site) => site.serverId === allSites.serverId);
      }

      const isSelectedPublish = subscribedSite !== undefined && subscribedSite !== null && subscribedSite.isPublished;
      const isSelectedSubscribed = subscribedSite !== undefined && subscribedSite !== null;

      siteData = {
        defaultUrl: allSites.defaultUrl,
        description: allSites.description,
        environmentId: allSites.environmentId,
        globalWebsiteId: allSites.globalWebsiteId,
        isPublished: isSelectedPublish,
        isSubscribed: isSelectedSubscribed,
        parentServerId: allSites.parentServerId,
        parentWebsiteId: allSites.parentWebsiteId,
        pcfDomain: allSites.pcfDomain,
        pcfRoute: allSites.pcfRoute,
        serverId: allSites.serverId,
        serverName: allSites.serverName,
        websiteStatus: allSites.websiteStatus,
        websiteType: allSites.websiteType,
        websiteUrl: allSites.websiteUrl,
      };

      resultSites.push(siteData);

      if (allSites.childSites === undefined || allSites.childSites.length === 0) {
        return resultSites;
      }

      allSites.childSites.map((childSite) => {

        if (subscribedSites !== null && subscribedSites.length > 0) {
          subscribedSite = subscribedSites.find((site) => site.serverId === childSite.serverId);
        }

        siteData = {
          defaultUrl: childSite.defaultUrl,
          description: childSite.description,
          environmentId: childSite.environmentId,
          globalWebsiteId: childSite.globalWebsiteId,
          isPublished: false,
          isSubscribed: false,
          parentServerId: childSite.parentServerId,
          parentWebsiteId: childSite.parentWebsiteId,
          pcfDomain: childSite.pcfDomain,
          pcfRoute: childSite.pcfRoute,
          serverId: childSite.serverId,
          serverName: childSite.serverName,
          websiteStatus: childSite.websiteStatus,
          websiteType: childSite.websiteType,
          websiteUrl: childSite.websiteUrl,
        };

        if (subscribedSite !== undefined && subscribedSite !== null) {
          siteData.isPublished = subscribedSite.isPublished;
          siteData.isSubscribed = true;
        }

        resultSites.push(siteData);
      });
    }

    return resultSites;
  }

  public postFeedWebsites = async (feedId, sites: any = []) =>
    await axios.post(
      `${_.trimEnd(this.config.newsroomApiBaseUrl, "/")}/api/v2/FeedWebsites/${this.config.environmentId}/${this.config.districtServerId}/${feedId}`,
      sites,
      await this.getConfig(),
    )

  private getConfig = async (invalidateCachedToken: boolean = false) => {
    if (this.config.role === "user") {
      return {
        headers: { Authorization: "Bearer" },
      };
    }
    return {
      headers: { Authorization: `Bearer ${(await this.getAccessToken(invalidateCachedToken)) || ""}` },
    };
  }
}
